import { Business, Group, Storage } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  getAdminCounters,
  getRecentlyBlockedUsers,
  getValidSessionUsers,
} from "../../api/app";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { AdminCounters } from "../../types/model/stats";
import { User } from "../../types/model/user";
import { UserResponse } from "../../types/model/user/UserResponse";
import { StoreInterfaces } from "../../types/store";
import { formatBytes } from "../../utils";
import UserTable from "../Users/UserTable";
import StatCard from "./StatCard";

const AdminDashboard = () => {
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();

  const [counters, setCounters] = useState<AdminCounters>();
  const [countersLoading, setCountersLoading] = useState(false);

  const [blockedUsers, setBlockedUsers] = useState<UserResponse[]>([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(false);

  const [validSessionUsers, setValidSessionUsers] = useState<UserResponse[]>([]);
  const [validSessionUsersLoading, setValidSessionUsersLoading] =
    useState(false);

  const navigate = useNavigate();
  const session = useSelector((state: StoreInterfaces) => state.session);

  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      const data = await getAdminCounters();
      setCounters(data);
    } catch (error) {
      handleError(error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchRecentlyBlockedUsers = async () => {
    setBlockedUsersLoading(true);
    try {
      const data = await getRecentlyBlockedUsers();
      setBlockedUsers(data);
    } catch (error) {
      handleError(error);
    } finally {
      setBlockedUsersLoading(false);
    }
  };

  const fetchValidSessionUsers = async () => {
    setValidSessionUsersLoading(true);
    try {
      const data = await getValidSessionUsers();
      setValidSessionUsers(data);
    } catch (error) {
      handleError(error);
    } finally {
      setValidSessionUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchCounters();
    fetchRecentlyBlockedUsers();
    fetchValidSessionUsers();
  }, []);

  const handleRequestRefreshBlockedUsers = () => {
    fetchRecentlyBlockedUsers();
  };

  const handleRequestRefreshValidSessionUsers = () => {
    fetchValidSessionUsers();
  };

  const handleUserRowClick = (user: User) => {
    if (session.isAdministrator) {
      navigate(`/admin/users/${user.accountId}/${user.id}`);
    }
  };

  let countersContent;
  if (countersLoading) {
    countersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (counters) {
    countersContent = (
      <Grid2
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(4) }}
      >
        <Grid2 key="counters-entities" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<Business />}
            title={t("generic.stats.entities")}
            total={counters.entities.total}
            active={counters.entities.active}
          />
        </Grid2>
        <Grid2 key="counters-users" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<Group />}
            title={t("generic.stats.users")}
            total={counters.users.total}
            active={counters.users.active}
          />
        </Grid2>
        <Grid2 key="counters-fileUsage" size={{ xs: 12, sm: 6, lg: 4 }}>
          <StatCard
            icon={<Storage />}
            title={t("generic.stats.file-usage")}
            total={formatBytes(counters.fileUsage.total)}
          />
        </Grid2>
      </Grid2>
    );
  } else {
    countersContent = (
      <Typography align="center" sx={{ mt: 2 }}>
        {t("generic.stats.no-data")}
      </Typography>
    );
  }

  let blockedUsersContent;
  if (blockedUsersLoading) {
    blockedUsersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (blockedUsers.length > 0) {
    blockedUsersContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.blocked-users")}
        </Typography>
        <UserTable
          tableData={blockedUsers}
          onRequestRefresh={handleRequestRefreshBlockedUsers}
          onRowClick={handleUserRowClick}
          showAddButton={false}
          disablePagination
        />
      </>
    );
  }

  let validSessionUsersContent;
  if (validSessionUsersLoading) {
    validSessionUsersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (validSessionUsers.length > 0) {
    validSessionUsersContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.valid-session-users")}
        </Typography>
        <UserTable
          tableData={validSessionUsers}
          onRequestRefresh={handleRequestRefreshValidSessionUsers}
          onRowClick={handleUserRowClick}
          showAddButton={false}
          disablePagination
        />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Counters */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: 2,
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t("generic.stats.overview")}
        </Typography>

        {countersContent}

        <Divider sx={{ my: 1 }} />

        {/* Recently Blocked Users Table */}
        {blockedUsersContent}

        {/* Valid Session Users Table */}
        {validSessionUsersContent}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
